import * as stylex from '@stylexjs/stylex';
import { formatForDisplay } from '@tanstack/hotkeys';
import { useForm } from '@tanstack/react-form';
import { useHotkey } from '@tanstack/react-hotkeys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Outlet,
  useLocation,
  useNavigate,
  useRouteContext,
  useRouterState,
  createFileRoute,
  CatchBoundary,
  Navigate,
} from '@tanstack/react-router';
import {
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useFocusWithin } from 'react-aria';
import {
  Input as AriaInput,
  type Key,
  type Selection,
  TagGroup as AriaTagGroup,
  TagList as AriaTagList,
  TextField as AriaTextField,
  Toolbar,
  Group,
  MenuTrigger,
  Pressable,
  TooltipTrigger,
  DialogTrigger,
  OverlayTriggerStateContext,
  type ListBoxProps,
} from 'react-aria-components';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { pick as rPick } from 'remeda';

import { DotBadge } from '@/components/dot-badge.component';
import { ErrorState } from '@/components/error-state.component';
import { Logo } from '@/components/logo.component';
import { NotFoundState } from '@/components/not-found-state.component';
import { LOCAL_STORAGE, SESSION_STORAGE } from '@/constants/storage';
import { useTrackSignIn } from '@/hooks/use-track-sign-in.hook';
import {
  FeatureEnum,
  OrganizationUserChangeRequestTypeEnum,
  OrganizationUserInvitationStatusEnum,
  OrganizationUserRoleEnum,
  OrganizationUserStatusEnum,
  type components,
} from '@/libs/openapi';
import {
  Avatar,
  AVATAR_SLOTS,
  type AvatarProps,
  Button,
  BUTTON_SLOTS,
  ChevronDownStrokeIcon,
  CloseStrokeIcon,
  CopyStrokeIcon,
  Dialog,
  DIALOG_SLOTS,
  DialogBar,
  EmptyState,
  Heading,
  HelpCircleStrokeIcon,
  HorizontalNavigation,
  HORIZONTAL_NAVIGATION_SLOTS,
  HorizontalNavigationContext,
  IconButton,
  Image,
  Kbd,
  List,
  ListBox,
  LIST_BOX_ITEM_SLOTS,
  ListItem,
  LockStrokeIcon,
  MegaphoneStrokeIcon,
  Menu,
  MenuItem,
  NavigationSide,
  NAVIGATION_SIDE_SLOTS,
  Paragraph,
  Popover,
  SettingsStrokeIcon,
  ShareStrokeIcon,
  SidebarExpandedStrokeIcon,
  SignOutStrokeIcon,
  Skeleton,
  StatisticsStrokeIcon,
  Tag,
  Text,
  TextButton,
  TEXT_BUTTON_SLOTS,
  TOAST_TIMEOUT,
  Tooltip,
  useCopyToClipboard,
  useLocalStorage,
  useMediaQuery,
  VerticalNavigation,
  VERTICAL_NAVIGATION_SLOTS,
  View,
  tryCatch,
  ClockRewindStrokeIcon,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { $ } from '@/libs/serenity-ui/tokens.stylex';
import { RouterIconButtonLink, RouterListBoxItemLink, RouterMenuItemLink } from '@/libs/tanstack-router';
import { auditLogPolicies } from '@/resources/audit-log/audit-log.policies';
import { campaignRequestMutations, campaignRequestQueries } from '@/resources/campaign-request/campaign-request.api';
import { campaignRequestPolicies } from '@/resources/campaign-request/campaign-request.policies';
import { campaignRequestProgramMutations, campaignRequestProgramQueries } from '@/resources/campaign-request-program/campaign-request-program.api';
import { campaignRequestProgramPolicies } from '@/resources/campaign-request-program/campaign-request-program.policies';
import { organizationUserMutations, organizationUserQueries } from '@/resources/organization-user/organization-user.api';
import { organizationUserApiKeyPolicies } from '@/resources/organization-user-api-key/organization-user-api-key.policies';
import { organizationUserInvitationMutations, organizationUserInvitationQueries } from '@/resources/organization-user-invitation/organization-user-invitation.api';
import { organizationUserInvitationPolicies } from '@/resources/organization-user-invitation/organization-user-invitation.policies';
import { reportPolicies } from '@/resources/report/report.policies';
import { userMutations } from '@/resources/user/user.api';
import { formatEnumValue } from '@/utils/format.util';
import { timezone } from '@/utils/timezone.util';

export const Route = createFileRoute('/_main')({
  component: RouteComponent,
  pendingComponent: PendingComponent,
  pendingMs: 0,
  errorComponent: ErrorState,
  notFoundComponent: NotFoundState,
  beforeLoad: ({ context }) => {
    // Return a stub `can` synchronously when user/organization are not yet available.
    // RouterProvider always renders (even before auth resolves) to avoid a blank screen
    // during OIDC initialization. This means the first load cycle runs with
    // context.user = undefined, so we must not try to bind policies here.
    //
    // Once OIDC and the /me queries resolve, main.tsx calls router.invalidate(), which
    // starts a fresh load cycle with the real context. Because this beforeLoad is
    // synchronous, there are no stale async continuations in flight — the new cycle runs
    // cleanly and writes the real `can` value.
    if (!context.organization || !context.user) {
      return {
        programsFeatureEnabled: false,
        can: {
          auditLog: { view: () => false },
          campaignRequests: { view: () => false },
          campaignRequestPrograms: { view: () => false },
          reports: { view: () => false },
          apiKeys: { view: () => false },
          invitation: { create: () => false },
        },
      };
    }

    const programsFeatureEnabled =
      context.user.original.featureFlags?.find((f) => f.feature === FeatureEnum.CAMPAIGN_REQUEST_PROGRAM)?.enabled ?? false;

    return {
      programsFeatureEnabled,
      can: {
        auditLog: rPick(auditLogPolicies.bindContext({ user: context.user.original }), [ 'view' ]),
        campaignRequests: rPick(campaignRequestPolicies.bindContext({ user: context.user.original, organization: context.organization.original }), [ 'view' ]),
        campaignRequestPrograms: rPick(campaignRequestProgramPolicies.bindContext({ user: context.user.original, organization: context.organization.original }), [ 'view' ]),
        reports: rPick(reportPolicies.bindContext({ user: context.user.original, organization: context.organization.original }), [ 'view' ]),
        apiKeys: rPick(organizationUserApiKeyPolicies.bindContext({ user: context.user.original, organization: context.organization.original }), [ 'view' ]),
        invitation: rPick(organizationUserInvitationPolicies.bindContext({ user: context.user.original }), [ 'create' ]),
      },
    };
  },
  head: () => ({
    meta: [
      {
        name: 'theme-color',
        media: '(prefers-color-scheme: light)',
        content: '#FFFFFF',
      },
      {
        name: 'theme-color',
        media: '(prefers-color-scheme: dark)',
        content: '#09090A',
      },
    ],
  }),
});

function PendingComponent() {
  return (
    <View style={pendingStyles.base}>
      <Image alt="Loading" height={50} src="/trendmd-loader.svg" width={90} />
    </View>
  );
}

const pendingStyles = stylex.create({
  base: {
    alignItems: 'center',
    display: 'flex',
    height: '100dvh',
    justifyContent: 'center',
    width: '100dvw',
  },
});

function RouteComponent() {
  const id = useId();

  const auth = useAuth();
  const hasInitiatedSignin = useRef(false);

  useEffect(() => {
    if (!hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading && !hasInitiatedSignin.current) {
      hasInitiatedSignin.current = true;
      void auth.signinRedirect();
    }
  }, [ auth ]);

  useTrackSignIn({ isAuthenticated: auth.isAuthenticated });

  const routeContext = Route.useRouteContext();
  // resolvedLocation is used intentionally instead of useLocation() or state.location.
  // Both of those update inside React.startTransition (before new route matches are committed),
  // so changing the CatchBoundary key at that point causes Outlet to re-render the old
  // (erroring) route component — the freshly-mounted boundary catches the same error again.
  // resolvedLocation is set only after all loaders complete and state.matches is fully
  // committed, so by the time the key changes, Outlet renders the new route safely.
  const routerStatePathname = useRouterState({ select: (s) => (s.resolvedLocation ?? s.location).pathname });
  const isLargeScreen = useMediaQuery(breakpoints.large);

  const [ isHorizontalNavigationOpen, setIsHorizontalNavigationOpen ] = useState(false);
  const [ isVerticalNavigationOpen, setIsVerticalNavigationOpen ] = useLocalStorage(
    LOCAL_STORAGE.navigationClosed.key,
    LOCAL_STORAGE.navigationClosed.initialValue,
  );

  useHotkey('Mod+\\', (event) => {
    event.preventDefault();
    setIsVerticalNavigationOpen((prev) => !prev);
  }, { enabled: isLargeScreen });

  if (routeContext.info) {
    return <Navigate to="/info" />;
  }

  // during the first load cycle when context.user/organization are not
  // yet available. Show the spinner until
  // router.invalidate() triggers a fresh cycle that populates `can`.
  // This replaces the framework-level pendingComponent for this specific case because
  // the framework pendingComponent only shows while beforeLoad is actively async;
  // our beforeLoad is synchronous by design
  if (!routeContext.user) {
    return <PendingComponent />;
  }

  return (
    <View style={styles.base}>
      {isLargeScreen ?
        <>
          <VerticalNavigation collapseToRail isOpen={isVerticalNavigationOpen} onOpenChange={setIsVerticalNavigationOpen}>
            {(renderProps) => (renderProps.isOpen ?
              <>
                <Toolbar slot={VERTICAL_NAVIGATION_SLOTS.LOGO_TOOLBAR}>
                  <Logo slot={VERTICAL_NAVIGATION_SLOTS.LOGO} />
                  <TooltipTrigger>
                    <IconButton slot={VERTICAL_NAVIGATION_SLOTS.CLOSE_ICON_BUTTON} />
                    <Tooltip placement="bottom">
                      <Text>Close sidebar</Text>
                      <Kbd>{formatForDisplay('Mod+\\')}</Kbd>
                    </Tooltip>
                  </TooltipTrigger>
                </Toolbar>

                <NavigationList />

                <Toolbar slot={VERTICAL_NAVIGATION_SLOTS.AVATAR_TOOLBAR}>
                  <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
                    <AvatarMenu key={`${id}-avatar`} />
                    <HelpIconButton />
                    {routeContext.can.invitation.create() && <InviteIconButton />}
                  </CatchBoundary>
                </Toolbar>
              </> :
              <>
                <TooltipTrigger>
                  <IconButton slot={VERTICAL_NAVIGATION_SLOTS.LOGO_OPEN_ICON_BUTTON}>
                    {(iconButtonRenderProps) => (iconButtonRenderProps.isHovered || iconButtonRenderProps.isFocusVisible ?
                      <SidebarExpandedStrokeIcon /> :
                      <Logo isCompact />)}
                  </IconButton>
                  <Tooltip placement="right">
                    <Text>Open sidebar</Text>
                    <Kbd>{formatForDisplay('Mod+\\')}</Kbd>
                  </Tooltip>
                </TooltipTrigger>

                <Toolbar>
                  <NavigationList isCompact />
                </Toolbar>

                <Toolbar slot={VERTICAL_NAVIGATION_SLOTS.AVATAR_TOOLBAR}>
                  <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
                    {routeContext.can.invitation.create() && <InviteIconButton />}
                    <HelpIconButton />
                    <AvatarMenu key={`${id}-avatar`} />
                  </CatchBoundary>
                </Toolbar>
              </>)}
          </VerticalNavigation>

          <main {...stylex.props(mainStyles.base)}>
            <CatchBoundary getResetKey={() => routerStatePathname}>
              <Outlet />
            </CatchBoundary>
          </main>
        </> :
        <>
          <HorizontalNavigationContext value={{ isOpen: isHorizontalNavigationOpen, onOpenChange: setIsHorizontalNavigationOpen }}>
            <HorizontalNavigation>
              <Group slot={HORIZONTAL_NAVIGATION_SLOTS.LEFT}>
                <IconButton slot={HORIZONTAL_NAVIGATION_SLOTS.HAMBURGER_BUTTON} />
                <Logo />
              </Group>

              <Group slot={HORIZONTAL_NAVIGATION_SLOTS.RIGHT}>
                <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
                  {routeContext.can.invitation.create() && <InviteIconButton />}
                  <HelpIconButton />
                  <AvatarMenu key={`${id}-avatar`} />
                </CatchBoundary>
              </Group>
            </HorizontalNavigation>

            <NavigationSide>
              <Group slot={NAVIGATION_SIDE_SLOTS.LOGO_GROUP}>
                <Logo />
              </Group>

              <NavigationList onListBoxAction={() => setIsHorizontalNavigationOpen(false)} />
            </NavigationSide>
          </HorizontalNavigationContext>

          <main {...stylex.props(mobileMainStyles.base)}>
            <CatchBoundary getResetKey={() => routerStatePathname}>
              <Outlet />
            </CatchBoundary>
          </main>
        </>}

      <TimezoneChangeDetector storedTimezone={routeContext.user.original.timezone ?? ''} />
    </View>
  );
}

interface NavigationListProps {
  /** Whether icons list should be rendered (default: false)  */
  isCompact?: boolean;
  onListBoxAction?: ListBoxProps<unknown>['onAction'];
}

function NavigationList({ isCompact, onListBoxAction }: NavigationListProps) {
  const location = useLocation();
  const routeContext = Route.useRouteContext();

  return isCompact ?
    <>
      {routeContext.can.reports.view() &&
        <TooltipTrigger>
          <RouterIconButtonLink aria-label="Metrics" to="/metrics">
            <StatisticsStrokeIcon />
          </RouterIconButtonLink>
          <Tooltip placement="right">Metrics</Tooltip>
        </TooltipTrigger>}
      {location.pathname.startsWith('/programs')
        ? routeContext.programsFeatureEnabled && routeContext.can.campaignRequestPrograms.view() &&
          <TooltipTrigger>
            <RouterIconButtonLink aria-label="Campaigns" to="/programs">
              <MegaphoneStrokeIcon />
            </RouterIconButtonLink>
            <Tooltip placement="right">Campaigns</Tooltip>
          </TooltipTrigger>
        : routeContext.can.campaignRequests.view() &&
        <TooltipTrigger>
          <RouterIconButtonLink aria-label="Campaigns" to="/campaigns">
            <MegaphoneStrokeIcon />
          </RouterIconButtonLink>
          <Tooltip placement="right">Campaigns</Tooltip>
        </TooltipTrigger>}
      {routeContext.can.apiKeys.view() &&
        <TooltipTrigger>
          <RouterIconButtonLink aria-label="API keys" to="/api-keys">
            <LockStrokeIcon />
          </RouterIconButtonLink>
          <Tooltip placement="right">API keys</Tooltip>
        </TooltipTrigger>}
      {routeContext.can.auditLog.view() &&
        <TooltipTrigger>
          <RouterIconButtonLink aria-label="Activity" to="/activity">
            <ClockRewindStrokeIcon />
          </RouterIconButtonLink>
          <Tooltip placement="right">Activity</Tooltip>
        </TooltipTrigger>}
    </> :
    <ListBox selectedKeys={[ [ '/metrics', '/campaigns', '/programs', '/api-keys', '/activity' ].find((base) => location.pathname === base || location.pathname.startsWith(base + '/')) ?? (location.pathname.startsWith('/programs') ? '/campaigns' : location.pathname) ]} selectionMode="single" onAction={onListBoxAction}>
      {routeContext.can.reports.view() &&
        <RouterListBoxItemLink id="/metrics" textValue="Analytics" to="/metrics">
          <StatisticsStrokeIcon />
          <Text slot={LIST_BOX_ITEM_SLOTS.LABEL}>Analytics</Text>
        </RouterListBoxItemLink>}
      {location.pathname.startsWith('/programs')
        ? routeContext.programsFeatureEnabled && routeContext.can.campaignRequestPrograms.view() &&
          <RouterListBoxItemLink id="/programs" textValue="Campaigns" to="/programs">
            <MegaphoneStrokeIcon />
            <Text slot={LIST_BOX_ITEM_SLOTS.LABEL}>Campaigns</Text>
          </RouterListBoxItemLink>
        : routeContext.can.campaignRequests.view() &&
          <RouterListBoxItemLink id="/campaigns" textValue="Campaigns" to="/campaigns">
            <MegaphoneStrokeIcon />
            <Text slot={LIST_BOX_ITEM_SLOTS.LABEL}>Campaigns</Text>
          </RouterListBoxItemLink>}
      {routeContext.can.apiKeys.view() &&
        <RouterListBoxItemLink id="/api-keys" textValue="API keys" to="/api-keys">
          <LockStrokeIcon />
          <Text slot={LIST_BOX_ITEM_SLOTS.LABEL}>API keys</Text>
        </RouterListBoxItemLink>}
      {routeContext.can.auditLog.view() &&
        <RouterListBoxItemLink id="/activity" textValue="Activity" to="/activity">
          <ClockRewindStrokeIcon />
          <Text slot={LIST_BOX_ITEM_SLOTS.LABEL}>Activity</Text>
        </RouterListBoxItemLink>}
    </ListBox>;
}

function AvatarMenu(props: AvatarProps) {
  const routeContext = Route.useRouteContext();
  const isLargeScreen = useMediaQuery(breakpoints.large);

  const location = useLocation();
  const auth = useAuth();

  return (
    <MenuTrigger>
      <TooltipTrigger isDisabled={!routeContext.user.meta.fullName}>
        <Pressable>
          <Avatar isDecorative role="button" {...props} style={avatarStyles.base}>
            {routeContext.user.original.avatar?.url ?
              <Image alt="Profile photo" src={routeContext.user.original.avatar.url} /> :
              routeContext.user.meta.initials &&
                <Text slot={routeContext.user.meta.initials.length === 2 ? AVATAR_SLOTS.TWO_LETTER_INITIALS_TEXT : AVATAR_SLOTS.ONE_LETTER_INITIALS_TEXT}>
                  {routeContext.user.meta.initials}
                </Text>}
          </Avatar>
        </Pressable>
        <Tooltip placement={isLargeScreen ? 'right' : 'left'}>{routeContext.user.meta.fullName}</Tooltip>
      </TooltipTrigger>

      <Popover>
        <Menu defaultSelectedKeys={[ location.href ]} selectionMode="single">
          <RouterMenuItemLink id="/settings" textValue="Settings" to="/settings">
            <SettingsStrokeIcon />
            <Text>Settings</Text>
          </RouterMenuItemLink>
          <MenuItem id="signOut" onAction={() => auth.signoutRedirect().then(auth.removeUser)}>
            <SignOutStrokeIcon />
            <Text>Sign out</Text>
          </MenuItem>
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}

function HelpIconButton() {
  const routeContext = Route.useRouteContext();

  const [ , copyToClipboard ] = useCopyToClipboard();

  return (
    <DialogTrigger>
      <View style={helpIconWrapStyles.base}>
        <IconButton aria-label="Help">
          <HelpCircleStrokeIcon />
        </IconButton>
        {!!routeContext.organization.original.coverageMember && (
          <View aria-hidden style={helpIconWrapStyles.dot}>
            <DotBadge size={7} />
          </View>
        )}
      </View>
      <Popover isNonModal placement="top">
        <View style={contextualHelpStyles.base}>
          {routeContext.organization.original.coverageMember ? (
            <>
              <View style={contextualHelpStyles.helpView}>
                <Text size={16} style={contextualHelpStyles.nameText} weight="medium">{routeContext.organization.meta.coverageMemberFullName}</Text>
                <TextButton
                  size={40}
                  style={contextualHelpStyles.textButton}
                  variant="tertiary"
                  onPress={() => copyToClipboard(routeContext.organization.original.coverageMember?.email ?? '').then(() => routeContext.toastQueue.add(
                    { message: 'Copied to clipboard', variant: 'success' },
                    { timeout: TOAST_TIMEOUT },
                  ))}>
                  <Text style={contextualHelpStyles.textButtonEmail}>{routeContext.organization.original.coverageMember?.email}</Text>
                  <CopyStrokeIcon size={16} slot={TEXT_BUTTON_SLOTS.RIGHT_ICON} />
                </TextButton>
              </View>
              <Text color="placeholder" size={14} style={contextualHelpStyles.coverageText}>
                Covering for {routeContext.organization.meta.accountManagerFullName} until{' '}
                {routeContext.organization.meta.coverageLeaveUntilFormatted}. <br /> TrendMD account manager. Email for support.
              </Text>
            </>
          ) : routeContext.organization.original.accountManager ? (
            <>
              <View style={contextualHelpStyles.helpView}>
                <Text size={16} style={contextualHelpStyles.nameText} weight="medium">{routeContext.organization.meta.accountManagerFullName}</Text>
                <TextButton
                  size={40}
                  style={contextualHelpStyles.textButton}
                  variant="tertiary"
                  onPress={() => copyToClipboard(routeContext.organization.original.accountManager?.email ?? '').then(() => routeContext.toastQueue.add(
                    { message: 'Copied to clipboard', variant: 'success' },
                    { timeout: TOAST_TIMEOUT },
                  ))}>
                  <Text style={contextualHelpStyles.textButtonEmail}>{routeContext.organization.original.accountManager?.email}</Text>
                  <CopyStrokeIcon size={16} slot={TEXT_BUTTON_SLOTS.RIGHT_ICON} />
                </TextButton>
              </View>
              <Text color="placeholder" size={14}>TrendMD account manager.<br /> Email for support.</Text>
            </>
          ) : (
            <>
              <View style={contextualHelpStyles.helpView}>
                <Text size={16} weight="medium">No account manager</Text>
                <TextButton
                  size={40}
                  style={contextualHelpStyles.textButton}
                  variant="tertiary"
                  onPress={() => copyToClipboard('support@trendmd.com').then(() => routeContext.toastQueue.add(
                    { message: 'Copied to clipboard', variant: 'success' },
                    { timeout: TOAST_TIMEOUT },
                  ))}>
                  <Text>support@trendmd.com</Text>
                  <CopyStrokeIcon size={16} slot={TEXT_BUTTON_SLOTS.RIGHT_ICON} />
                </TextButton>
              </View>
              <Text color="placeholder" size={14}>TrendMD support.<br /> Email to get one assigned to your organization.</Text>
            </>
          )}
        </View>
      </Popover>
    </DialogTrigger>
  );
}

function TimezoneChangeDetector ({ storedTimezone }: { storedTimezone: string }) {
  const { toastQueue } = useRouteContext({ from: '/_main' });
  const updateMeMutation = useMutation(userMutations.updateMe);

  const browserIana = timezone.normalize(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const curatedMatch = timezone.findCurated(browserIana);

  const [ isOpen, setIsOpen ] = useState(
    () =>
      curatedMatch !== null &&
      curatedMatch.iana !== storedTimezone &&
      !sessionStorage.getItem(SESSION_STORAGE.timezonePromptDismissed.key),
  );

  if (!curatedMatch) {
    return null;
  }

  return (
    <Dialog
      isDismissable
      isOpen={isOpen}
      style={dialogStyles.base}
      onOpenChange={(open) => {
        if (!open) {
          sessionStorage.setItem(SESSION_STORAGE.timezonePromptDismissed.key, '1');
        }
        setIsOpen(open);
      }}>
      {({ close }) => (
        <>
          <DialogBar slot={DIALOG_SLOTS.TOP_BAR}>
            <Heading>Update your timezone?</Heading>
            <IconButton slot={DIALOG_SLOTS.CLOSE} />
          </DialogBar>

          <Paragraph color="tertiary">
            We detected a timezone change to {curatedMatch.label}.
            {' '}Updating helps keep your time-based data accurate to your local time. Change anytime in Settings.
          </Paragraph>

          <DialogBar slot={DIALOG_SLOTS.BOTTOM_BAR}>
            <Button
              isPending={updateMeMutation.isPending}
              isRounded
              size={40}
              variant="primary"
              onPress={() => {
                updateMeMutation.mutate(
                  { timezone: curatedMatch.iana },
                  {
                    onSuccess: () => {
                      close();
                      toastQueue.add({ message: 'Timezone updated.', variant: 'success' }, { timeout: TOAST_TIMEOUT });
                    },
                    onError: (error) => {
                      close();
                      toastQueue.add({ message: error.message, variant: 'error' }, { timeout: TOAST_TIMEOUT });
                    },
                  },
                );
              }}>
              Update timezone
            </Button>
          </DialogBar>
        </>
      )}
    </Dialog>
  );
}

function InviteIconButton() {
  const isLargeScreen = useMediaQuery(breakpoints.large);

  return (
    <DialogTrigger>
      <TooltipTrigger>
        <IconButton aria-label="Invite users">
          {(renderProps) => <ShareStrokeIcon strokeWidth={renderProps.isPressed ? undefined : 0.7} />}
        </IconButton>
        <Tooltip placement={isLargeScreen ? 'right' : 'bottom'}>Invite users</Tooltip>
      </TooltipTrigger>
      <Dialog style={dialogStyles.base}>
        <InviteDialogContent />
      </Dialog>
    </DialogTrigger>
  );
}

interface EmailEntry {
  id: string;
  email: string;
}

const ROLE_OPTIONS: Record<string, string> = {
  [OrganizationUserRoleEnum.VIEWER]: 'Viewer',
  [OrganizationUserRoleEnum.EDITOR]: 'Editor',
  [OrganizationUserRoleEnum.ADMIN]: 'Admin',
};

type OwnedCampaignSummary = Pick<components['schemas']['CampaignRequestResponseDto'], 'id' | 'campaignName'>;
type OwnedProgramSummary = Pick<components['schemas']['CampaignRequestProgramResponseDto'], 'id' | 'name'>;

type PendingDialog =
  | { type: 'changeRole'; user: components['schemas']['OrganizationUserResponseDto']; newRole: OrganizationUserRoleEnum }
  | {
    type: 'assignOwner';
    mode: 'deactivate' | 'roleChange';
    user: components['schemas']['OrganizationUserResponseDto'];
    newRole?: OrganizationUserRoleEnum;
    campaigns: OwnedCampaignSummary[];
    programs: OwnedProgramSummary[];
  }
  | { type: 'deactivate'; user: components['schemas']['OrganizationUserResponseDto'] }
  | { type: 'confirmAdminInvite'; emails: EmailEntry[] };

const ROLE_RANK: Record<OrganizationUserRoleEnum, number> = {
  [OrganizationUserRoleEnum.ADMIN]: 3,
  [OrganizationUserRoleEnum.EDITOR]: 2,
  [OrganizationUserRoleEnum.VIEWER]: 1,
};

const isRoleDowngrade = (from: OrganizationUserRoleEnum, to: OrganizationUserRoleEnum): boolean =>
  ROLE_RANK[to] < ROLE_RANK[from];

function InviteDialogContent (): React.ReactElement {
  const { toastQueue, user: currentUser, organization } = useRouteContext({ from: '/_main' });
  const dialogState = useContext(OverlayTriggerStateContext);
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const [ pendingDialog, setPendingDialog ] = useState<PendingDialog | null>(null);
  const [ highlightedEmails, setHighlightedEmails ] = useState<ReadonlySet<string>>(new Set());

  const canManageUsers = currentUser.original.role === OrganizationUserRoleEnum.ADMIN;
  const accountManagerName = organization?.original.accountManager
    ? [ organization.original.accountManager.firstName, organization.original.accountManager.lastName ].filter(Boolean).join(' ') || undefined
    : undefined;

  const usersQuery = useQuery(organizationUserQueries.list());
  const invitationsQuery = useQuery(organizationUserInvitationQueries.list());

  const activeUsers = (usersQuery.data?.data ?? [])
    .map((d) => d.original)
    .filter((u) => u.status !== OrganizationUserStatusEnum.Value0);
  const pendingInvitations = (invitationsQuery.data ?? []).filter(
    (inv) => inv.status === OrganizationUserInvitationStatusEnum.PENDING || inv.status === OrganizationUserInvitationStatusEnum.PENDING_APPROVAL,
  );

  const updateUser = useMutation(organizationUserMutations.update);
  const createInvitation = useMutation(organizationUserInvitationMutations.create);
  const cancelInvitation = useMutation(organizationUserInvitationMutations.cancel);
  const resendInvitation = useMutation(organizationUserInvitationMutations.resend);

  const addToast = (message: string, variant: 'success' | 'info' | 'error' | 'warning'): void => {
    toastQueue.add({ message, variant }, { timeout: TOAST_TIMEOUT });
  };

  const closeDialog = (): void => {
    dialogState?.close();
  };

  const sendInvitations = async (emailEntries: EmailEntry[], role: OrganizationUserRoleEnum): Promise<void> => {
    const invitePromises = emailEntries.map((entry) =>
      createInvitation.mutateAsync({ email: entry.email, role }));
    const results = await Promise.allSettled(invitePromises);

    const succeededEmails = results
      .map((r, i) => (r.status === 'fulfilled' ? emailEntries[i].email : null))
      .filter((email): email is string => email !== null);

    const failed = results.filter((r) => r.status === 'rejected');

    // Explicitly refetch after mutations settle so newly-added rows appear immediately
    await queryClient.invalidateQueries({ queryKey: organizationUserInvitationQueries.all.queryKey });
    await queryClient.invalidateQueries({ queryKey: organizationUserQueries.all.queryKey });

    if (succeededEmails.length > 0) {
      setHighlightedEmails((prev) => new Set([ ...prev, ...succeededEmails ]));
      succeededEmails.forEach((email) => {
        setTimeout(() => {
          setHighlightedEmails((prev) => {
            const next = new Set(prev);
            next.delete(email);
            return next;
          });
        }, 5000);
      });
    }

    if (failed.length === 0) {
      addToast('Invitation sent', 'success');
    } else if (failed.length < emailEntries.length) {
      addToast(`${emailEntries.length - failed.length} invited, ${failed.length} failed`, 'warning');
    } else {
      const reason = (failed[0] as PromiseRejectedResult).reason;
      addToast(reason?.message ?? 'Failed to send invitations', 'error');
    }
  };

  const form = useForm({
    defaultValues: {
      emails: [] as EmailEntry[],
      role: String(OrganizationUserRoleEnum.EDITOR),
    },
    onSubmit: async ({ value }) => {
      if (value.emails.length === 0) {
        return;
      }

      const role = Number(value.role) as OrganizationUserRoleEnum;

      if (role === OrganizationUserRoleEnum.ADMIN) {
        setPendingDialog({ type: 'confirmAdminInvite', emails: value.emails });
        return;
      }

      await sendInvitations(value.emails, role);
      form.reset();
    },
  });

  const fetchOwnedPrograms = async (userId: string): Promise<OwnedProgramSummary[]> => {
    const result = await tryCatch(queryClient.fetchQuery(
      campaignRequestProgramQueries.list({
        paging: { pageIndex: 0, pageSize: 100 },
      }),
    ));
    if (result.error) {
      return [];
    }
    return result.data.data
      .filter((p) => p.owner?.id === userId)
      .map((p) => ({ id: p.id, name: p.name }));
  };

  const handleRoleChange = async (
    user: components['schemas']['OrganizationUserResponseDto'],
    newRole: OrganizationUserRoleEnum,
  ): Promise<void> => {
    const isDowngrade = isRoleDowngrade(user.role, newRole);
    const isAdminInvolved = user.role === OrganizationUserRoleEnum.ADMIN || newRole === OrganizationUserRoleEnum.ADMIN;

    if (isDowngrade && newRole === OrganizationUserRoleEnum.VIEWER) {
      const [ campaignsResult, ownedPrograms ] = await Promise.all([
        tryCatch(queryClient.fetchQuery(
          campaignRequestQueries.list({
            filter: { ownerId: user.id },
            paging: { pageIndex: 0, pageSize: 100 },
          }),
        )),
        fetchOwnedPrograms(user.id),
      ]);

      const ownedCampaigns: OwnedCampaignSummary[] = campaignsResult.error ? [] : campaignsResult.data.data;

      if (ownedCampaigns.length > 0 || ownedPrograms.length > 0) {
        setPendingDialog({ type: 'assignOwner', mode: 'roleChange', user, newRole, campaigns: ownedCampaigns, programs: ownedPrograms });
        return;
      }
    }

    if (isAdminInvolved) {
      setPendingDialog({ type: 'changeRole', user, newRole });
      return;
    }

    updateUser.mutate(
      { id: user.id, body: { role: newRole }},
      {
        onSuccess: () => {
          addToast(`Role changed to ${formatEnumValue(newRole, OrganizationUserRoleEnum)}`, 'success');
        },
      },
    );
  };

  const handleDeactivate = async (
    user: components['schemas']['OrganizationUserResponseDto'],
  ): Promise<void> => {
    const [ campaignsResult, ownedPrograms ] = await Promise.all([
      tryCatch(queryClient.fetchQuery(
        campaignRequestQueries.list({
          filter: { ownerId: user.id },
          paging: { pageIndex: 0, pageSize: 100 },
        }),
      )),
      fetchOwnedPrograms(user.id),
    ]);

    const ownedCampaigns: OwnedCampaignSummary[] = campaignsResult.error ? [] : campaignsResult.data.data;

    if (ownedCampaigns.length > 0 || ownedPrograms.length > 0) {
      setPendingDialog({ type: 'assignOwner', mode: 'deactivate', user, campaigns: ownedCampaigns, programs: ownedPrograms });
      return;
    }

    setPendingDialog({ type: 'deactivate', user });
  };

  if (pendingDialog?.type === 'changeRole') {
    return (
      <ChangeRoleDialogContent
        newRole={pendingDialog.newRole}
        user={pendingDialog.user}
        onClose={() => setPendingDialog(null)}
      />
    );
  }

  if (pendingDialog?.type === 'assignOwner') {
    const { user: targetUser, mode, newRole, campaigns, programs } = pendingDialog;
    const isAdminInvolved =
      targetUser.role === OrganizationUserRoleEnum.ADMIN || (newRole !== undefined && newRole === OrganizationUserRoleEnum.ADMIN);

    return (
      <AssignOwnerDialogContent
        activeNonViewerUsers={activeUsers.filter((u) => u.role !== OrganizationUserRoleEnum.VIEWER)}
        campaigns={campaigns}
        mode={mode}
        newRole={newRole}
        programs={programs}
        user={targetUser}
        onClose={() => setPendingDialog(null)}
        onContinue={() => {
          if (mode === 'deactivate') {
            setPendingDialog({ type: 'deactivate', user: targetUser });
            return;
          }

          if (isAdminInvolved) {
            setPendingDialog({ type: 'changeRole', user: targetUser, newRole: newRole as OrganizationUserRoleEnum });
            return;
          }

          updateUser.mutate(
            { id: targetUser.id, body: { role: newRole }},
            {
              onSuccess: () => {
                addToast(`Role changed to ${formatEnumValue(newRole as OrganizationUserRoleEnum, OrganizationUserRoleEnum)}`, 'success');
                setPendingDialog(null);
              },
              onError: () => setPendingDialog(null),
            },
          );
        }}
      />
    );
  }

  if (pendingDialog?.type === 'deactivate') {
    return (
      <DeactivateUserDialogContent
        accountManagerName={accountManagerName}
        user={pendingDialog.user}
        onClose={() => setPendingDialog(null)}
      />
    );
  }

  if (pendingDialog?.type === 'confirmAdminInvite') {
    const { emails } = pendingDialog;
    return (
      <>
        <DialogBar slot={DIALOG_SLOTS.TOP_BAR}>
          <Heading>TrendMD approval required</Heading>
          <IconButton aria-label="Close" slot={DIALOG_SLOTS.CLOSE} onPress={() => setPendingDialog(null)} />
        </DialogBar>

        <View style={adminInviteStyles.body}>
          <Paragraph color="tertiary" size={16}>
            This admin invite will be active after approval by{' '}
            {accountManagerName ? `${accountManagerName}, your` : 'your'} TrendMD account manager.
          </Paragraph>
        </View>

        <DialogBar slot={DIALOG_SLOTS.BOTTOM_BAR}>
          <Button
            isPending={createInvitation.isPending}
            isRounded
            size={40}
            onPress={async () => {
              await sendInvitations(emails, OrganizationUserRoleEnum.ADMIN);
              setPendingDialog(null);
              form.reset();
            }}>
            Invite
          </Button>
        </DialogBar>
      </>
    );
  }

  return (
    <>
      <DialogBar slot={DIALOG_SLOTS.TOP_BAR}>
        <Heading>Invite users</Heading>
        <View style={inviteDialogTopBarActionsStyles.base}>
          <TooltipTrigger>
            <IconButton
              aria-label="Learn about user roles"
              size={32}
              variant="ghost"
              onPress={() => {
                closeDialog();
                navigate({ to: '/invite/guide' });
              }}>
              <HelpCircleStrokeIcon />
            </IconButton>
            <Tooltip>Learn about user roles</Tooltip>
          </TooltipTrigger>
          <IconButton aria-label="Close" size={32} variant="ghost" onPress={closeDialog}>
            <CloseStrokeIcon />
          </IconButton>
        </View>
      </DialogBar>

      <form
        {...stylex.props(InviteDialogContentStyles.base)}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}>
        <View style={invitationFieldViewStyles.base}>
          <form.Field name="emails">
            {(emailsField) => (
              <form.Field name="role">
                {(roleField) => (
                  <InvitationField
                    selectedRole={roleField.state.value as Key}
                    value={emailsField.state.value}
                    onRoleChange={(role) => roleField.handleChange(role as string)}
                    onValueChange={(emails) => emailsField.handleChange(emails)}
                  />
                )}
              </form.Field>
            )}
          </form.Field>
          <form.Subscribe selector={(state) => state.values.emails.length > 0 && !state.isSubmitting}>
            {(canSubmit) => (
              <Button isDisabled={!canSubmit} isRounded size={48} type="submit">Invite</Button>
            )}
          </form.Subscribe>
        </View>
      </form>

      <List style={listStyles.container}>
        {(usersQuery.isLoading || invitationsQuery.isLoading) ? (
          <>
            <ListItem style={listStyles.row}>
              <Skeleton style={skeletonStyles.avatar} />
              <View style={listStyles.nameSection}>
                <Skeleton style={skeletonStyles.name} />
                <Skeleton style={skeletonStyles.email} />
              </View>
              <Skeleton style={skeletonStyles.role} />
            </ListItem>
            <ListItem style={listStyles.row}>
              <Skeleton style={skeletonStyles.avatar} />
              <View style={listStyles.nameSection}>
                <Skeleton style={skeletonStyles.name} />
                <Skeleton style={skeletonStyles.email} />
              </View>
              <Skeleton style={skeletonStyles.role} />
            </ListItem>
            <ListItem style={listStyles.row}>
              <Skeleton style={skeletonStyles.avatar} />
              <View style={listStyles.nameSection}>
                <Skeleton style={skeletonStyles.name} />
                <Skeleton style={skeletonStyles.email} />
              </View>
              <Skeleton style={skeletonStyles.role} />
            </ListItem>
          </>
        ) : (
          <>
            {activeUsers.filter((au) => au.id !== currentUser.original.id).map((user) => {
              const joinedName = [ user.firstName, user.lastName ].filter(Boolean).join(' ');
              const fullName = joinedName ? joinedName : user.email;
              const trimmedInitials = `${user.firstName?.charAt(0)?.toUpperCase() ?? ''}${user.lastName?.charAt(0)?.toUpperCase() ?? ''}`.trim();
              const initials = trimmedInitials ? trimmedInitials : user.email.charAt(0).toUpperCase();
              const pendingChange = user.pendingChangeRequest?.type != null ? user.pendingChangeRequest : null;

              let nameDisplay = fullName;
              if (pendingChange?.type === OrganizationUserChangeRequestTypeEnum.CHANGE_ROLE) {
                nameDisplay = `${fullName} (role change pending)`;
              } else if (pendingChange?.type === OrganizationUserChangeRequestTypeEnum.DEACTIVATE) {
                nameDisplay = `${fullName} (deactivation pending)`;
              }

              return (
                <ListItem key={user.id} style={listStyles.row}>
                  {({ isHovered, isFocusWithin }) => {
                    const isActive = isHovered || isFocusWithin;
                    return (
                      <>
                        <Avatar isDecorative size={32}>
                          {user.avatar?.url ? (
                            <Image alt={fullName} src={user.avatar.url} />
                          ) : initials ? (
                            <Text slot={initials.length >= 2 ? AVATAR_SLOTS.TWO_LETTER_INITIALS_TEXT : AVATAR_SLOTS.ONE_LETTER_INITIALS_TEXT}>
                              {initials.slice(0, 2)}
                            </Text>
                          ) : null}
                        </Avatar>

                        <View style={listStyles.nameSection}>
                          <Text color={isActive ? 'primary' : 'secondary'} size={14} style={listStyles.nameText}>{nameDisplay}</Text>
                          <Text color="tertiary" size={12} style={listStyles.subText}>{user.email}</Text>
                        </View>

                        <View style={listStyles.action}>
                          <MenuTrigger>
                            <TextButton isDisabled={!canManageUsers || !!pendingChange} size={40}>
                              <Text>{formatEnumValue(user.role, OrganizationUserRoleEnum)}</Text>
                              <ChevronDownStrokeIcon slot={TEXT_BUTTON_SLOTS.RIGHT_ICON} />
                            </TextButton>
                            <Popover>
                              <Menu
                                disallowEmptySelection
                                selectedKeys={new Set([ String(user.role) ])}
                                selectionMode="single"
                                onSelectionChange={(keys) => {
                                  if (keys === 'all') {
                                    return;
                                  }
                                  const selectedKey = keys.values().next().value as Key;
                                  if (selectedKey === 'deactivate') {
                                    return;
                                  }
                                  const newRole = Number(selectedKey) as OrganizationUserRoleEnum;
                                  if (newRole === user.role) {
                                    return;
                                  }
                                  void handleRoleChange(user, newRole);
                                }}>
                                <MenuItem id={String(OrganizationUserRoleEnum.VIEWER)}>Viewer</MenuItem>
                                <MenuItem id={String(OrganizationUserRoleEnum.EDITOR)}>Editor</MenuItem>
                                <MenuItem id={String(OrganizationUserRoleEnum.ADMIN)}>Admin</MenuItem>
                                <MenuItem id="deactivate" onAction={() => void handleDeactivate(user)}>Deactivate</MenuItem>
                              </Menu>
                            </Popover>
                          </MenuTrigger>
                        </View>
                      </>
                    );
                  }}
                </ListItem>
              );
            })}

            {pendingInvitations.map((invitation) => (
              <ListItem
                key={invitation.id}
                style={[ listStyles.row, highlightedEmails.has(invitation.email) && listStyles.rowHighlighted ]}>
                {({ isHovered, isFocusWithin }) => {
                  const isActive = isHovered || isFocusWithin;
                  return (
                    <>
                      <Avatar isDecorative size={32} />

                      <View style={listStyles.nameSection}>
                        <Text color={isActive ? 'primary' : 'secondary'} size={14} style={listStyles.nameText}>{invitation.email}</Text>
                        <Text color="tertiary" size={12}>{invitation.status === OrganizationUserInvitationStatusEnum.PENDING_APPROVAL ? 'Waiting for TrendMD approval' :'Invite sent'}</Text>
                      </View>

                      <View style={listStyles.action}>
                        {canManageUsers ?
                          invitation.status === OrganizationUserInvitationStatusEnum.PENDING_APPROVAL ?
                            null : (
                              <MenuTrigger>
                                <TextButton size={40}>
                                  <Text>{formatEnumValue(invitation.role, OrganizationUserRoleEnum)}</Text>
                                  <ChevronDownStrokeIcon slot={TEXT_BUTTON_SLOTS.RIGHT_ICON} />
                                </TextButton>
                                <Popover>
                                  <Menu>
                                    <MenuItem
                                      onAction={async () => {
                                        try {
                                          await resendInvitation.mutateAsync(invitation.id);
                                          addToast('Invitation resent', 'success');
                                        } catch (err) {
                                          addToast((err as Error)?.message ?? 'Failed to resend invitation', 'error');
                                        }
                                      }}>
                                      Resend invitation
                                    </MenuItem>
                                    <MenuItem
                                      onAction={async () => {
                                        try {
                                          await cancelInvitation.mutateAsync(invitation.id);
                                          addToast('Invitation cancelled', 'success');
                                        } catch (err) {
                                          addToast((err as Error)?.message ?? 'Failed to cancel invitation', 'error');
                                        }
                                      }}>
                                      Cancel invitation
                                    </MenuItem>
                                  </Menu>
                                </Popover>
                              </MenuTrigger>
                            ) : (
                            <Text color="secondary" size={14}>{formatEnumValue(invitation.role, OrganizationUserRoleEnum)}</Text>
                          )}
                      </View>
                    </>
                  );
                }}
              </ListItem>
            ))}
          </>
        )}
      </List>
    </>
  );
}

const InviteDialogContentStyles = stylex.create({
  base: {
    paddingBottom: 16,
  },
});

const invitationFieldViewStyles = stylex.create({
  base: {
    alignItems: 'center',
    alignSelf: 'stretch',
    display: 'flex',
    gap: 8,
    paddingRight: 8,
  },
});

const adminInviteStyles = stylex.create({
  body: {
    width: 413,
  },
});

const inviteDialogTopBarActionsStyles = stylex.create({
  base: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: 4,
  },
});

const listStyles = stylex.create({
  container: {
    maxHeight: 320,
    overflowY: 'auto',
    paddingBottom: 8,
    scrollbarWidth: 'none',
  },
  row: {
    alignItems: 'center',
    backgroundColor: {
      default: 'transparent',
      ':hover': $.opacity04,
      ':focus-within': $.opacity04,
    },
    borderRadius: 8,
    display: 'flex',
    gap: 8,
    height: 56,
    outline: 'none',
    padding: 8,
    transitionDuration: '600ms',
    transitionProperty: 'background-color',
    transitionTimingFunction: 'ease',
  },
  nameSection: {
    display: 'flex',
    flex: '1 1 0',
    flexDirection: 'column',
    minWidth: 0,
    overflow: 'hidden',
  },
  nameText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  subText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  action: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: 4,
  },
  rowHighlighted: {
    backgroundColor: {
      default: $.accentSun10,
      ':hover': $.accentSun10,
      ':focus-within': $.accentSun10,
    },
  },
});

const skeletonStyles = stylex.create({
  avatar: {
    borderRadius: 9999,
    flexShrink: 0,
    height: 32,
    width: 32,
  },
  name: {
    borderRadius: 4,
    height: 14,
    width: 120,
  },
  email: {
    borderRadius: 4,
    height: 12,
    marginTop: 2,
    width: 160,
  },
  role: {
    borderRadius: 4,
    height: 28,
    width: 72,
  },
});

interface InvitationFieldProps {
  value: EmailEntry[];
  onValueChange: (emails: EmailEntry[]) => void;
  inputValue?: string;
  onInputValueChange?: (value: string) => void;
  selectedRole: Key;
  onRoleChange: (role: Key) => void;
}

function InvitationField ({
  value: emails,
  onValueChange: setEmails,
  inputValue: controlledInputValue,
  onInputValueChange: controlledOnInputValueChange,
  selectedRole,
  onRoleChange,
}: InvitationFieldProps): React.ReactElement {
  const [ uncontrolledInputValue, setUncontrolledInputValue ] = useState('');
  const inputValue = controlledInputValue ?? uncontrolledInputValue;
  const setInputValue = controlledOnInputValueChange ?? setUncontrolledInputValue;

  const [ isFocusWithin, setIsFocusWithin ] = useState(false);

  const { focusWithinProps } = useFocusWithin({
    onFocusWithinChange: setIsFocusWithin,
  });

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const addEmail = (value: string): void => {
    const email = value.trim().replace(/,$/, '');
    if (email && EMAIL_REGEX.test(email) && !emails.some((e) => e.email === email)) {
      setEmails([ ...emails, { id: crypto.randomUUID(), email }]);
    }
  };

  const tryCommit = (value: string): boolean => {
    const trimmed = value.trim().replace(/[, ]+$/, '');
    if (trimmed && EMAIL_REGEX.test(trimmed)) {
      addEmail(trimmed);
      setInputValue('');
      return true;
    }
    return false;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Tab' && inputValue.trim()) {
      // Commit on Tab only if valid — keep focus in input by preventing default
      const trimmed = inputValue.trim().replace(/,$/, '');
      if (EMAIL_REGEX.test(trimmed)) {
        e.preventDefault();
        tryCommit(inputValue);
      }
    }
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      tryCommit(inputValue);
    }
    if ((e.key === ',' || e.key === ' ') && inputValue.trim()) {
      const trimmed = inputValue.trim().replace(/[, ]+$/, '');
      if (EMAIL_REGEX.test(trimmed)) {
        e.preventDefault();
        tryCommit(inputValue);
      }
      // Not a valid email yet — let character appear in input naturally
    }
    if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      setEmails(emails.slice(0, -1));
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
    const pasted = e.clipboardData.getData('text');
    const pastedEmails = pasted.split(/[,;\s\n]+/).filter(Boolean);
    if (pastedEmails.length > 1) {
      e.preventDefault();
      let updated = [ ...emails ];
      pastedEmails.forEach((raw) => {
        const email = raw.trim().replace(/,$/, '');
        if (email && EMAIL_REGEX.test(email) && !updated.some((entry) => entry.email === email)) {
          updated = [ ...updated, { id: crypto.randomUUID(), email }];
        }
      });
      setEmails(updated);
    }
  };

  const handleRemoveTags = (keys: Set<Key>): void => {
    setEmails(emails.filter((e) => !keys.has(e.id)));
  };

  const handleRoleChange = (keys: Selection): void => {
    if (keys !== 'all') {
      onRoleChange(keys.values().next().value as Key);
    }
  };

  return (
    <div
      data-focus-within={isFocusWithin || undefined}
      data-has-emails={emails.length > 0 || undefined}
      {...focusWithinProps}
      {...stylex.props(fieldStyles.container, emails.length > 0 && fieldStyles.containerWithEmails)}
      onClick={(e) => {
        const target = e.target as HTMLElement;
        if (!target.closest('button, [role="menu"], [role="menuitem"], [role="menuitemradio"]')) {
          (e.currentTarget.querySelector('input') as HTMLInputElement | null)?.focus();
        }
      }}>
      <div {...stylex.props(fieldStyles.inputArea)}>
        {emails.length > 0 && (
          <AriaTagGroup
            aria-label="Email addresses"
            {...stylex.props(fieldStyles.tagGroupContents)}
            onRemove={handleRemoveTags}>
            <AriaTagList
              items={emails}
              {...stylex.props(fieldStyles.tagListContents)}>
              {(item) => (
                <Tag
                  id={item.id}
                  size={28}
                  variant="tertiary">
                  {item.email}
                </Tag>
              )}
            </AriaTagList>
          </AriaTagGroup>
        )}
        <AriaTextField
          aria-label="Email input"
          autoComplete="off"
          isReadOnly={emails.length > 0}
          value={inputValue}
          {...stylex.props(fieldStyles.textFieldContents)}
          onChange={setInputValue}>
          <AriaInput
            autoComplete="off"
            data-1p-ignore
            data-bwignore
            data-form-type="other"
            data-lpignore="true"
            data-protonpass-ignore
            placeholder={emails.length === 0 ? 'Email' : ''}
            {...stylex.props(fieldStyles.nativeInput)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onBlur={() => {
              const trimmed = inputValue.trim().replace(/,$/, '');
              if (trimmed && EMAIL_REGEX.test(trimmed)) {
                addEmail(trimmed);
                setInputValue('');
              }
            }}
          />
        </AriaTextField>
      </div>

      {emails.length > 0 && (
        <MenuTrigger>
          <Button size={28} variant="quaternary">
            <Text>{ROLE_OPTIONS[selectedRole as string] ?? String(selectedRole)}</Text>
            <ChevronDownStrokeIcon slot={BUTTON_SLOTS.RIGHT} />
          </Button>
          <Popover>
            <Menu
              disallowEmptySelection
              selectedKeys={new Set([ selectedRole ])}
              selectionMode="single"
              onSelectionChange={handleRoleChange}>
              <MenuItem id={String(OrganizationUserRoleEnum.VIEWER)}>Viewer</MenuItem>
              <MenuItem id={String(OrganizationUserRoleEnum.EDITOR)}>Editor</MenuItem>
              <MenuItem id={String(OrganizationUserRoleEnum.ADMIN)}>Admin</MenuItem>
            </Menu>
          </Popover>
        </MenuTrigger>
      )}

      {(emails.length > 0 || inputValue.trim()) && (
        <IconButton
          aria-label="Clear input"
          isRounded
          size={40}
          variant="ghost"
          onPress={() => {
            setEmails([]);
            setInputValue('');
          }}>
          <CloseStrokeIcon />
        </IconButton>
      )}
    </div>
  );
}

const fieldStyles = stylex.create({
  container: {
    alignItems: 'center',
    backgroundColor: {
      default: $.surfaceBGPrimary,
      ':is([data-focus-within])': $.opacity04,
      ':is(:hover):not([data-focus-within])': $.opacity04,
      ':is([data-has-emails][data-focus-within])': $.surfaceBGPrimary,
      ':is([data-has-emails]:hover):not([data-focus-within])': $.surfaceBGPrimary,
    },
    borderColor: {
      default: $.border16,
      ':is([data-focus-within])': 'transparent',
      ':hover': 'transparent',
      ':is([data-has-emails])': $.border16,
      ':is([data-has-emails][data-focus-within])': $.borderInteractive,
      ':is([data-has-emails]:hover):not([data-focus-within])': $.border24,
    },
    borderRadius: 999,
    borderStyle: 'solid',
    borderWidth: {
      default: 1,
      ':is([data-focus-within])': 2,
    },
    boxSizing: 'border-box',
    cursor: 'text',
    display: 'flex',
    gap: 2,
    height: 48,
    overflow: 'hidden',
    paddingBlock: {
      default: 14,
      ':is([data-focus-within])': 13,
    },
    paddingLeft: {
      default: 16,
      ':is([data-focus-within])': 15,
    },
    paddingRight: {
      default: 4,
      ':is([data-focus-within])': 3,
    },
    width: '100%',
  },
  containerWithEmails: {},
  inputArea: {
    alignItems: 'center',
    display: 'flex',
    flex: '1 1 0',
    flexWrap: 'nowrap',
    gap: 6,
    minWidth: 0,
    overflowX: 'auto',
    scrollbarWidth: 'none',
  },
  tagGroupContents: {
    display: 'contents',
  },
  tagListContents: {
    display: 'contents',
  },
  textFieldContents: {
    display: 'contents',
  },
  nativeInput: {
    backgroundColor: 'transparent',
    borderStyle: 'none',
    boxSizing: 'border-box',
    caretColor: $.textInteractive,
    color: $.textPrimary,
    flex: '1 1 120px',
    fontFamily: 'Inter',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: 400,
    height: 32,
    letterSpacing: -0.32,
    minWidth: 0,
    outline: 'none',
    overflow: 'hidden',
    padding: 0,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    '::placeholder': { color: $.textPlaceholder },
    '::selection': { backgroundColor: $.surfaceHighlightMedium },
  },
});

interface ChangeRoleDialogContentProps {
  user: components['schemas']['OrganizationUserResponseDto'];
  newRole: OrganizationUserRoleEnum;
  onClose: () => void;
}

function ChangeRoleDialogContent ({
  user,
  newRole,
  onClose,
}: ChangeRoleDialogContentProps): React.ReactElement {
  const { toastQueue } = useRouteContext({ from: '/_main' });

  const fullName = [ user.firstName, user.lastName ].filter(Boolean).join(' ') || user.email;

  const updateMutation = useMutation(organizationUserMutations.update);

  const handleConfirm = async (): Promise<void> => {
    await updateMutation.mutateAsync({ id: user.id, body: { role: newRole }});
    toastQueue.add({ message:`${fullName}'s role change is pending TrendMD approval`, variant: 'error' }, { timeout: TOAST_TIMEOUT });
    onClose();
  };

  return (
    <>
      <DialogBar slot={DIALOG_SLOTS.TOP_BAR}>
        <Heading style={dialogHeadingStyles.truncated}>Change {fullName}&apos;s role</Heading>
        <IconButton
          aria-label="Close"
          size={32}
          style={changeRoleCloseButtonStyles.base}
          variant="ghost"
          onPress={onClose}>
          <CloseStrokeIcon />
        </IconButton>
      </DialogBar>

      <View>
        <Paragraph color="tertiary" size={16}>
          Admin role changes require approval from your TrendMD Account manager.
          <br />
          <br />
          The update will apply after approval.
        </Paragraph>
      </View>

      <DialogBar slot={DIALOG_SLOTS.BOTTOM_BAR}>
        <Button
          isPending={updateMutation.isPending}
          isRounded
          size={40}
          onPress={handleConfirm}>
          Change role
        </Button>
      </DialogBar>
    </>
  );
}

const changeRoleCloseButtonStyles = stylex.create({
  base: {
    position: 'absolute',
    right: 10,
    top: 16,
  },
});

interface AssignOwnerDialogContentProps {
  user: components['schemas']['OrganizationUserResponseDto'];
  activeNonViewerUsers: components['schemas']['OrganizationUserResponseDto'][];
  campaigns: OwnedCampaignSummary[];
  programs: OwnedProgramSummary[];
  mode: 'deactivate' | 'roleChange';
  newRole?: OrganizationUserRoleEnum;
  onClose: () => void;
  onContinue: () => void;
}

type OwnedItem =
  | { kind: 'campaign'; id: number; label: string }
  | { kind: 'program'; id: number; label: string };

function AssignOwnerDialogContent ({
  user,
  activeNonViewerUsers,
  campaigns,
  programs,
  mode,
  newRole,
  onClose,
  onContinue,
}: AssignOwnerDialogContentProps): React.ReactElement {
  const fullName = [ user.firstName, user.lastName ].filter(Boolean).join(' ') || user.email;
  const newRoleLabel = newRole !== undefined ? formatEnumValue(newRole, OrganizationUserRoleEnum) : '';
  const heading = mode === 'roleChange'
    ? `Change ${fullName}'s role to ${newRoleLabel}`
    : `Deactivate ${fullName}`;
  const description = mode === 'roleChange'
    ? `To change ${fullName}'s role, assign the following campaigns/programs to a different account.`
    : `To deactivate ${fullName}'s account, assign the following campaigns/programs to a different account.`;

  const items: OwnedItem[] = [
    ...campaigns.map((c): OwnedItem => ({ kind: 'campaign', id: c.id, label: c.campaignName })),
    ...programs.map((p): OwnedItem => ({ kind: 'program', id: p.id, label: p.name })),
  ];

  // `${kind}:${id}` → selectedOwnerId (UUID)
  const [ selectedOwners, setSelectedOwners ] = useState<ReadonlyMap<string, string>>(new Map());
  const [ isPending, setIsPending ] = useState(false);

  const updateCampaign = useMutation(campaignRequestMutations.update);
  const updateProgram = useMutation(campaignRequestProgramMutations.update);

  const eligibleUsers = activeNonViewerUsers.filter((u) => u.id !== user.id);

  const itemKey = (item: OwnedItem): string => `${item.kind}:${item.id}`;

  const getOwnerLabel = (item: OwnedItem): string => {
    const selectedId = selectedOwners.get(itemKey(item));
    if (selectedId) {
      const selectedUser = eligibleUsers.find((u) => u.id === selectedId);
      return selectedUser
        ? [ selectedUser.firstName, selectedUser.lastName ].filter(Boolean).join(' ') || selectedUser.email
        : fullName;
    }
    return fullName;
  };

  const handleSelect = (item: OwnedItem, newOwnerId: string): void => {
    setSelectedOwners((prev) => new Map(prev).set(itemKey(item), newOwnerId));
  };

  const handleContinue = async (): Promise<void> => {
    setIsPending(true);
    await Promise.all(
      items.map((item) => {
        const newOwnerId = selectedOwners.get(itemKey(item));
        if (!newOwnerId) {
          return Promise.resolve();
        }
        if (item.kind === 'campaign') {
          return updateCampaign.mutateAsync({ id: item.id, body: { ownerUserId: newOwnerId }});
        }
        return updateProgram.mutateAsync({ id: item.id, body: { ownerUserId: newOwnerId }});
      }),
    );
    onContinue();
    setIsPending(false);
  };

  const allReassigned = items.length > 0 && items.every((item) => selectedOwners.has(itemKey(item)));

  return (
    <>
      <DialogBar slot={DIALOG_SLOTS.TOP_BAR}>
        <Heading style={dialogHeadingStyles.truncated}>{heading}</Heading>
        <IconButton
          aria-label="Close"
          size={32}
          style={assignOwnerCloseButtonStyles.base}
          variant="ghost"
          onPress={onClose}>
          <CloseStrokeIcon />
        </IconButton>
      </DialogBar>

      <View style={assignOwnerStyles.body}>
        <Paragraph color="tertiary" size={16} style={assignOwnerStyles.paragraph}>
          {description}
        </Paragraph>

        <List style={assignOwnerStyles.list}>
          {items.map((item) => (
            <ListItem key={itemKey(item)} style={assignOwnerStyles.row}>
              {({ isHovered, isFocusWithin }) => (
                <>
                  <View style={assignOwnerStyles.itemInfo}>
                    <Text
                      color={(isHovered || isFocusWithin) ? 'primary' : 'secondary'}
                      size={14}
                      style={assignOwnerStyles.itemName}>
                      {item.label}
                    </Text>
                    <Text color="quaternary" size={12}>{item.kind === 'campaign' ? 'Campaign' : 'Program'}</Text>
                  </View>
                  <MenuTrigger>
                    <TextButton size={40} style={assignOwnerStyles.ownerButton}>
                      <Text style={assignOwnerStyles.ownerButtonText}>{getOwnerLabel(item)}</Text>
                      <ChevronDownStrokeIcon slot={TEXT_BUTTON_SLOTS.RIGHT_ICON} />
                    </TextButton>
                    <Popover>
                      <Menu
                        items={eligibleUsers}
                        renderEmptyState={() =>
                          <EmptyState style={assignOwnerEmptyStateStyles.base}>
                            <Heading>No eligible users found</Heading>
                            <Paragraph>Please invite a new user first</Paragraph>
                          </EmptyState>}>
                        {(u) => {
                          const name = [ u.firstName, u.lastName ].filter(Boolean).join(' ') || u.email;
                          return (
                            <MenuItem
                              id={u.id}
                              onAction={() => handleSelect(item, u.id)}>
                              {name}
                            </MenuItem>
                          );
                        }}
                      </Menu>
                    </Popover>
                  </MenuTrigger>
                </>
              )}
            </ListItem>
          ))}
        </List>
      </View>

      <DialogBar slot={DIALOG_SLOTS.BOTTOM_BAR}>
        <Button
          isDisabled={!allReassigned}
          isPending={isPending}
          isRounded
          size={40}
          onPress={() => void handleContinue()}>
          Continue
        </Button>
      </DialogBar>
    </>
  );
}

const assignOwnerStyles = stylex.create({
  body: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    gap: 16,
    minHeight: 0,
    overflow: 'hidden',
    width: {
      default: 480,
      [breakpoints.small]: '100%',
    },
  },
  paragraph: {
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
  ownerButton: {
    flexShrink: 0,
    maxWidth: 200,
    overflow: 'hidden',
  },
  ownerButtonText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  list: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    minHeight: 0,
    overflowY: 'auto',
  },
  row: {
    alignItems: 'center',
    backgroundColor: {
      default: 'transparent',
      ':hover': $.opacity04,
      ':focus-within': $.opacity04,
    },
    borderRadius: 8,
    display: 'flex',
    gap: 8,
    height: 56,
    outline: 'none',
    padding: 8,
    transitionDuration: '600ms',
    transitionProperty: 'background-color',
    transitionTimingFunction: 'ease',
  },
  itemInfo: {
    display: 'flex',
    flex: '1 1 0',
    flexDirection: 'column',
    minWidth: 0,
  },
  itemName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

const assignOwnerCloseButtonStyles = stylex.create({
  base: {
    position: 'absolute',
    right: 10,
    top: 16,
  },
});

const assignOwnerEmptyStateStyles = stylex.create({
  base: {
    padding: 8,
  },
});

interface DeactivateUserDialogContentProps {
  user: components['schemas']['OrganizationUserResponseDto'];
  accountManagerName: string | undefined;
  onClose: () => void;
}

function DeactivateUserDialogContent ({
  user,
  accountManagerName,
  onClose,
}: DeactivateUserDialogContentProps): React.ReactElement {
  const { toastQueue } = useRouteContext({ from: '/_main' });
  const queryClient = useQueryClient();

  const fullName = [ user.firstName, user.lastName ].filter(Boolean).join(' ') || user.email;
  const requiresApproval = user.role === OrganizationUserRoleEnum.ADMIN || user.role === OrganizationUserRoleEnum.EDITOR;

  const updateMutation = useMutation(organizationUserMutations.update);

  const handleConfirm = async (): Promise<void> => {
    const result = await updateMutation.mutateAsync({
      id: user.id,
      body: {
        status: OrganizationUserStatusEnum.Value0,
      },
    });

    if (result.status === 202) {
      toastQueue.add({ message:`${fullName} deactivation pending TrendMD approval`, variant: 'error' }, { timeout: TOAST_TIMEOUT });
    } else {
      toastQueue.add({ message:`${fullName} deactivated`, variant: 'success' }, { timeout: TOAST_TIMEOUT });
    }

    await queryClient.invalidateQueries({ queryKey: organizationUserQueries.all.queryKey });
    onClose();
  };

  return (
    <>
      <DialogBar slot={DIALOG_SLOTS.TOP_BAR}>
        <Heading style={dialogHeadingStyles.truncated}>{requiresApproval ? 'TrendMD approval required' : `Deactivate ${fullName}`}</Heading>
        <IconButton
          aria-label="Close"
          size={32}
          style={deactivateCloseButtonStyles.base}
          variant="ghost"
          onPress={onClose}>
          <CloseStrokeIcon />
        </IconButton>
      </DialogBar>

      <View style={deactivateStyles.body}>
        {requiresApproval ? (
          <Paragraph color="tertiary" size={16} style={deactivateStyles.paragraph}>
            This deactivation requires approval from{accountManagerName ? ` ${accountManagerName}, your` : ' your'} TrendMD account manager.
            <br />
            <br />
            Once approved, {fullName} will be permanently removed from the TrendMD Dashboard.
            <br />
            <br />
            This action can&apos;t be undone.
          </Paragraph>
        ) : (
          <Paragraph color="tertiary" size={16} style={deactivateStyles.paragraph}>
            {fullName} will be permanently removed from the TrendMD Dashboard.
            <br />
            <br />
            This action can&apos;t be undone.
          </Paragraph>
        )}
      </View>

      <DialogBar slot={DIALOG_SLOTS.BOTTOM_BAR}>
        <Button
          isPending={updateMutation.isPending}
          isRounded
          size={40}
          onPress={handleConfirm}>
          Deactivate
        </Button>
      </DialogBar>
    </>
  );
}

const deactivateStyles = stylex.create({
  body: {
    width: {
      default: 480,
      [breakpoints.small]: '100%',
    },
  },
  paragraph: {
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
});

const dialogHeadingStyles = stylex.create({
  truncated: {
    maxWidth: 400,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

const deactivateCloseButtonStyles = stylex.create({
  base: {
    position: 'absolute',
    right: 10,
    top: 16,
  },
});

const styles = stylex.create({
  base: {
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: {
      default: 'column',
      [breakpoints.large]: 'row',
    },
    height: {
      default: '100dvh',
      [breakpoints.large]: null,
    },
    minHeight: {
      default: null,
      [breakpoints.large]: '100vh',
    },
  },
});

const mainStyles = stylex.create({
  base: {
    overflowX: 'hidden',
    width: '100%',
  },
});

const mobileMainStyles = stylex.create({
  base: {
    display: 'flex',
    flex: '1',
    flexDirection: 'column',
    minHeight: 0,
    overflowY: 'auto',
    width: '100%',
  },
});

const avatarStyles = stylex.create({
  base: {
    cursor: 'pointer',
  },
});

const helpIconWrapStyles = stylex.create({
  base: {
    display: 'inline-flex',
    position: 'relative',
  },
  dot: {
    pointerEvents: 'none',
    position: 'absolute',
    right: 4,
    top: 6,
  },
});

const contextualHelpStyles = stylex.create({
  base: {
    alignItems: 'flex-start',
    backgroundColor: $.surfaceBGAlternative01,
    borderColor: $.border08,
    borderRadius: 10,
    borderStyle: 'solid',
    borderWidth: 1,
    boxShadow: $.shadowBottom060Medium,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    maxWidth: 390,
    minWidth: 242,
    overflow: 'hidden',
    padding: 20,
  },
  helpView: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '100%',
    overflow: 'hidden',
  },
  nameText: {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  textButton: {
    height: 32,
    maxWidth: '100%',
    overflow: 'hidden',
  },
  textButtonEmail: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  coverageText: {
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
});

// TODO: move to serenity UI
const dialogStyles = stylex.create({
  base: {
    margin: {
      default: 0,
      [breakpoints.small]: 8,
    },
    width: {
      default: 520,
      [breakpoints.small]: '100%',
    },
  },
});
